import { CommonModule } from '@angular/common'
import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { Store, select } from '@ngrx/store'
import { AccountObject, BUYER, ItemType, ProductCategory, ProductType, SUPPLIER } from '@tradecafe/types/core'
import { DeepReadonly, compareBy } from '@tradecafe/types/utils'
import { map as _map, groupBy, mapValues } from 'lodash-es'
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs'
import { map, tap, shareReplay, takeUntil } from 'rxjs/operators'
import { AuthApiService } from 'src/api/auth'
import { deferAfterRender } from 'src/shared/utils/defer-after-render'
import { loadItemTypes, selectItemTypeEntities } from 'src/app/store/item-types'
import { loadProductCategories, selectProductCategoryEntities } from 'src/app/store/product-categories'
import { loadProductTypes, selectProductTypeEntities } from 'src/app/store/product-types'
import { loadProducts, selectProductEntities } from 'src/app/store/products'
import { ReactiveAsteriskModule } from 'src/components/reactive-asterisk/reactive-asterisk.module'
import { isAtLeastAdmin } from 'src/services/data/users.service'
import { ModalModule } from 'src/shared/modal'
import { replayForm } from 'src/shared/utils/replay-form'
import { waitNotEmpty } from 'src/shared/utils/wait-not-empty'
import { CompanyFormGroup } from '../company-form'
import { CompanyProfileFormModule } from '../sub-forms/company-profile-form/company-profile-form.module'
import { CompanyProfileFormService, ProfileRow } from '../sub-forms/company-profile-form/company-profile-form.service'

@Component({
    selector: 'tc-company-profile-tab',
    templateUrl: './company-profile-tab.component.html',
    styleUrl: './company-profile-tab.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatCheckboxModule,
        ReactiveAsteriskModule,
        ReactiveFormsModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        ModalModule,
        CompanyProfileFormModule,
    ]
})
export class CompanyProfileTabComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly AuthApi = inject(AuthApiService)
  private readonly CompanyProfileForm = inject(CompanyProfileFormService)
  private readonly store = inject(Store)

  private readonly destroy$ = new Subject<void>()
  constructor() {
    // Defer store dispatches until skeleton loader has been painted
    deferAfterRender(() => {
      this.store.dispatch(loadProducts())
      this.store.dispatch(loadProductCategories())
      this.store.dispatch(loadProductTypes())
      this.store.dispatch(loadItemTypes())
    })
  }

  @Input({ required: true }) companyForm: CompanyFormGroup
  @Input({ required: true }) isBulkEdit: boolean
  @Input({ required: true }) company$: BehaviorSubject<DeepReadonly<AccountObject>>
  @Input({ required: true }) selectedCompanies$: BehaviorSubject<DeepReadonly<AccountObject[]>>

  protected readonly isAdminManagerSuperuser = isAtLeastAdmin(this.AuthApi.currentUser)
  protected readonly isJuniorAdmin = this.AuthApi.currentUser.role === 'junior-administrator'

  protected productRows$: Observable<DeepReadonly<{
    businessType: string;
    groups: {
      category: ProductCategory;
      groups: {
        type: ProductType;
        groups: {
          itemType: ItemType;
          productNames: string;
          groups: ProfileRow[];
        }[];
      }[];
    }[];
  }[]>>
  protected canSeeOfferCreationEnablement$: Observable<boolean>
  protected enablementLabel$: Observable<string>
  protected isLoading$ = new BehaviorSubject<boolean>(true)

  ngOnInit() {
    this.canSeeOfferCreationEnablement$ = (this.isBulkEdit || !this.isAdminManagerSuperuser && !this.isJuniorAdmin)
      ? of(false)
      : replayForm(this.companyForm.controls.type).pipe(map(type => type === SUPPLIER || type === BUYER))

    this.enablementLabel$ = replayForm(this.companyForm.controls.type).pipe(map(type => ({
      [BUYER]: 'Available Supplier Offer matching',
      [SUPPLIER]: 'Enable Supplier Offer Creation',
    }[type] || '')))
  }

  ngAfterViewInit() {
    this.productRows$ = this.buildProductRows().pipe(tap(() => this.isLoading$.next(false)), shareReplay(1))
    this.productRows$.pipe(takeUntil(this.destroy$)).subscribe()
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  protected async showAddProfile() {
    const res = await this.CompanyProfileForm.showAddProfile({
      company: this.company$.value,
      selectedCompanies: this.selectedCompanies$.value,
      isBulkEdit: this.isBulkEdit,
      rows: [],
    })
    this.handleCompanyProfileFormResult(res)
  }

  protected async showEditProfile(rows: DeepReadonly<ProfileRow[]>) {
    const res = await this.CompanyProfileForm.showEditProfile({
      company: this.company$.value,
      selectedCompanies: this.selectedCompanies$.value,
      isBulkEdit: this.isBulkEdit,
      rows,
    })
    this.handleCompanyProfileFormResult(res)
  }

  protected async showDeleteProfile(rows: DeepReadonly<ProfileRow[]>) {
    const res = await this.CompanyProfileForm.showDeleteProfile({
      company: this.company$.value,
      selectedCompanies: this.selectedCompanies$.value,
      isBulkEdit: this.isBulkEdit,
      rows,
    })
    this.handleCompanyProfileFormResult(res)
  }

  private handleCompanyProfileFormResult(res: DeepReadonly<AccountObject | AccountObject[]>) {
    if (!res) return
    if (this.isBulkEdit) {
      this.selectedCompanies$.next(res as AccountObject[])
    } else {
      const persisted = !!this.company$.value.account
      // refresh company$ for unsaved companies too — later dialogs read products_spec from it
      this.company$.next(res as AccountObject)
      const ctrl = this.companyForm.controls.products_spec
      ctrl.setValue((res as AccountObject).products_spec)
      ctrl.markAsTouched()
      if (persisted) ctrl.markAsPristine()
      else ctrl.markAsDirty()
      ctrl.updateValueAndValidity()
    }
  }

  private buildProductRows() {
    return combineLatest([
      this.store.pipe(select(selectProductEntities), waitNotEmpty()),
      this.store.pipe(select(selectProductTypeEntities), waitNotEmpty()),
      this.store.pipe(select(selectProductCategoryEntities), waitNotEmpty()),
      this.store.pipe(select(selectItemTypeEntities), waitNotEmpty()),
      replayForm(this.companyForm.controls.products_spec),
      this.selectedCompanies$
    ]).pipe(map(([products, types, categories, itemTypes, products_spec, selectedCompanies]) => {
      let commonProductSpecs: AccountObject['products_spec']

      // find common-info for bulk-edit
      if (this.isBulkEdit && selectedCompanies?.length === 1) {
        products_spec = selectedCompanies[0].products_spec
      } else if (this.isBulkEdit && selectedCompanies?.length > 1) {
        commonProductSpecs = this.findCommonProductSpecs()
      }

      // group products
      const interestingProducts = (commonProductSpecs || products_spec)?.map(profileLine => ({
        profileLine,
        product: products[profileLine.product_id],
        category: categories[products[profileLine.product_id]?.category_id],
        type: types[products[profileLine.product_id]?.type_id],
        itemType: itemTypes[profileLine.item_type_id],
        businessType: categories[products[profileLine.product_id]?.category_id]?.type,
        contactId: profileLine.contact_id,
      }))?.sort(compareBy(x => x.product?.name))
      const typeGroups = mapValues(groupBy(interestingProducts, 'product.type_id'), (groups, type_id) => ({
        type: types[type_id],
        groups: _map(groupBy(groups, 'profileLine.item_type_id'), (grps, item_type_id) => ({
          itemType: itemTypes[item_type_id],
          productNames: grps.map(p => p.product?.name || 'unknown').join(', '),
          groups: grps,
        })).sort(compareBy(x => x.itemType?.name)),
      }))
      const categoryGroups = mapValues(groupBy(typeGroups, 'type.category_id'), (groups, category_id) => ({
        category: categories[category_id],
        groups: groups.sort(compareBy(x => x.type?.name)),
      }))
      const businessTypeGroups = mapValues(groupBy(categoryGroups, 'category.type'), (groups, type) => ({
        businessType: type,
        groups: groups.sort(compareBy(x => x.category?.name)),
      }))

      return _map(businessTypeGroups).sort(compareBy(x => x.businessType))
    }))
  }

  private findCommonProductSpecs() {
    const findCommonality = (spec1, spec2) => {
      let commonSpecs = []
      if (Array.isArray(spec1) && Array.isArray(spec2)) {
        spec1.forEach((e) => {
          if (spec2.some((k) => k.product_id === e.product_id)) {
            commonSpecs.push(e)
          }
        })
      }
      return commonSpecs;
    }
    let commonInfo = []
    for (let i = 1; i < this.selectedCompanies$.value.length; i++) {
      commonInfo = findCommonality(this.selectedCompanies$.value[0].products_spec, this.selectedCompanies$.value[i].products_spec)
    }
    return commonInfo
  }
}
